import { useCallback, useMemo, useState } from 'react'
import { useMutation } from '@apollo/react-hooks'
import { useTranslation } from 'react-i18next'
import * as Mutation from '../../../api/gql/mutation'
import { subtreeIds, type Tree, type VisibleRow } from '../lib/tree'
import { useGanttStoreApi } from '../store/ganttStore'
import type { ScheduleActivity } from '../types'
import type { RowOps } from '../detail/row-ops'
import { useScheduleMutations } from './useScheduleMutations'

interface Deps {
  scheduleId: string
  tree: Tree<ScheduleActivity>
  byId: Map<string, ScheduleActivity>
  rows: VisibleRow<ScheduleActivity>[]
  rowIndexById: Map<string, number>
  selectedIds: string[]
  focusedId: string | null
  /** The single mutation queue for the whole detail page (see `GanttView`). */
  run: ReturnType<typeof useScheduleMutations>['run']
  focusActivity: (id: string, opts?: { openPanel?: boolean }) => void
  /** Review fix (Important #4, spec §10): gates `RowOps.canAdd` — Add activity / milestone /
   * sub-activity, unlike the rest of `RowOps`, need `canCreate` rather than `canEdit`. */
  canCreate: boolean
}

/**
 * Row-structure operations (spec B §9.5, Task 16): `rowOps` (add/indent/outdent/move/delete,
 * computed from `focusedId`/`selectedIds` and the tree) plus the delete-confirmation dialog's
 * state and handler. Extracted out of `GanttView` once it crossed the ~400-line guideline (the
 * same reason `useCellEditing` exists) — a pure move, no behaviour change.
 */
export const useRowOps = ({ scheduleId, tree, byId, rows, rowIndexById, selectedIds, focusedId, run, focusActivity, canCreate }: Deps) => {
  const { t } = useTranslation()
  const api = useGanttStoreApi()
  const [createActivity] = useMutation(Mutation.CREATE_SCHEDULE_ACTIVITY)
  const [moveActivity] = useMutation(Mutation.MOVE_SCHEDULE_ACTIVITY)
  const [deleteActivities] = useMutation(Mutation.DELETE_SCHEDULE_ACTIVITIES)
  const [deleteTargets, setDeleteTargets] = useState<string[] | null>(null)
  const [deleting, setDeleting] = useState(false)

  const rowOps: RowOps = useMemo(() => {
    const focused = focusedId ? tree.byId.get(focusedId) ?? null : null
    const siblings = focused ? (focused.parent ? focused.parent.children : tree.roots) : tree.roots
    const sibIndex = focused ? siblings.indexOf(focused) : -1
    const prevSibling = sibIndex > 0 ? siblings[sibIndex - 1] : null

    const addActivity = async (kind: 'task' | 'milestone' | 'child', anchorId?: string) => {
      // Task 3 (keyboard row entry): an explicit anchor overrides the memoised `focused`/`sibIndex`
      // above for this call only — Shift+Enter/Insert inside a cell editor fires before focus has
      // moved to that row, so `focusedId` may still point elsewhere (or nowhere).
      const anchor = anchorId ? tree.byId.get(anchorId) ?? null : focused
      const anchorSiblings = anchor ? (anchor.parent ? anchor.parent.children : tree.roots) : tree.roots
      const anchorSibIndex = anchor ? anchorSiblings.indexOf(anchor) : -1
      const input = kind === 'milestone'
        ? { name: t('Schedule_new_milestone'), isMilestone: true, durationDays: 0 }
        : { name: t('Schedule_new_activity'), durationDays: 1 }
      const where = kind === 'child' && anchor
        ? { parentId: anchor.activity.id, index: null }
        : anchor
          ? { parentId: anchor.activity.parentId, index: anchorSibIndex + 1 }
          : { parentId: null, index: null }
      try {
        const data: any = await run(
          () => createActivity({ variables: { scheduleId, input: { ...input, ...where } } }) as unknown as Promise<{ data: any }>,
          (d: any) => d.createScheduleActivity,
        )
        // the new row is the one not in the previous (pre-mutation) `byId` map
        const created = data.createScheduleActivity.changedActivities.find((a: any) => a.name === input.name && !byId.has(a.id))
        if (created) {
          if (kind === 'child' && anchor) api.getState().setCollapsed([anchor.activity.id], false)
          focusActivity(created.id)
          api.getState().setEditingCell({ activityId: created.id, column: 'name' })
        }
      } catch {
        /* run() already toasted the error */
      }
    }

    const move = (parentId: string | null, index: number) => {
      if (!focused) return
      run(
        () => moveActivity({ variables: { scheduleId, id: focused.activity.id, parentId, index } }) as unknown as Promise<{ data: any }>,
        (d: any) => d.moveScheduleActivity,
      ).catch(() => { /* run() already toasted the error */ })
    }
    const indent = () => { if (prevSibling) move(prevSibling.activity.id, prevSibling.children.length) }
    const outdent = () => {
      if (!focused || !focused.parent) return
      const grandSiblings = focused.parent.parent ? focused.parent.parent.children : tree.roots
      move(focused.parent.activity.parentId, grandSiblings.indexOf(focused.parent) + 1)
    }
    const moveUp = () => { if (focused && sibIndex > 0) move(focused.activity.parentId, sibIndex - 1) }
    const moveDown = () => { if (focused && sibIndex < siblings.length - 1) move(focused.activity.parentId, sibIndex + 1) }
    const remove = () => {
      const targets = selectedIds.length ? selectedIds : focusedId ? [focusedId] : []
      if (targets.length) setDeleteTargets(targets)
    }
    const goTo = (id: string) => focusActivity(id)

    return {
      addActivity, indent, outdent, moveUp, moveDown, remove, goTo,
      canIndent: !!prevSibling,
      canOutdent: !!(focused && focused.parent),
      canMoveUp: !!focused && sibIndex > 0,
      canMoveDown: !!focused && sibIndex < siblings.length - 1,
      canRemove: selectedIds.length > 0 || !!focusedId,
      canAdd: canCreate,
    }
  }, [focusedId, tree, selectedIds, byId, run, createActivity, moveActivity, scheduleId, t, api, focusActivity, canCreate])

  // Built once and reused for both the confirm dialog's count and the delete's own next-focus scan
  // (review fix: this used to be computed twice, once per consumer).
  const deletedIdSet = useMemo(
    () => (deleteTargets ? new Set(deleteTargets.flatMap((id) => subtreeIds(tree, id))) : null),
    [deleteTargets, tree],
  )
  const deleteCount = deletedIdSet ? deletedIdSet.size : 0

  const cancelDelete = useCallback(() => setDeleteTargets(null), [])

  const confirmDelete = useCallback(async () => {
    if (!deleteTargets || !deletedIdSet) return
    setDeleting(true)
    // focus moves to the row that followed the last deleted one, walking backward if it was the last row
    const lastIndex = Math.max(...deleteTargets.map((id) => rowIndexById.get(id) ?? -1))
    let nextFocus: string | null = null
    for (let i = lastIndex + 1; i < rows.length && !nextFocus; i++) if (!deletedIdSet.has(rows[i].node.activity.id)) nextFocus = rows[i].node.activity.id
    for (let i = lastIndex - 1; i >= 0 && !nextFocus; i--) if (!deletedIdSet.has(rows[i].node.activity.id)) nextFocus = rows[i].node.activity.id
    try {
      await run(
        () => deleteActivities({ variables: { scheduleId, ids: deleteTargets } }) as unknown as Promise<{ data: any }>,
        (d: any) => d.deleteScheduleActivities,
      )
      // `focusActivity` already replaces the selection down to just `nextFocus` (`select(id, 'replace', …)`);
      // `clearSelection()` is only needed in the `else` branch, where nothing calls `select` at all.
      if (nextFocus) {
        focusActivity(nextFocus)
      } else {
        api.getState().clearSelection()
        api.getState().setFocused(null)
      }
      setDeleteTargets(null)
    } catch {
      /* run() already toasted the error */
    } finally {
      setDeleting(false)
    }
  }, [deleteTargets, deletedIdSet, rowIndexById, rows, run, deleteActivities, scheduleId, api, focusActivity])

  return { rowOps, deleteTargets, deleteCount, deleting, confirmDelete, cancelDelete }
}
